import {
  RxDBInitialisationError,
  useRxDBContext,
} from "$global-providers/rxdb-provider/provider";
import { linkToSignOut } from "$routes/_lib/links";
import { Box, Button, styled, Typography } from "@material-ui/core";
import { useState } from "react";
import { Link } from "react-router-dom";
import {
  EPOCUS_BG,
  EPOCUS_TEXT_PRIMARY,
  EPOCUS_TEXT_SECONDARY,
  EPOCUS_WHITE,
  rem,
} from "./epocus-palette";

type BlockedCopy = {
  title: string;
  body: string;
  canRetry: boolean;
  resetLabel?: string;
};

const STORAGE_COPY: BlockedCopy = {
  title: "Couldn't unlock local storage",
  body: "ePOCUS stores your scans on this device and could not open that storage, so nothing here is up to date. Try again, and if it keeps happening, sign out and back in or contact support.",
  canRetry: true,
};

const BLOCKED_COPY: Record<RxDBInitialisationError, BlockedCopy> = {
  [RxDBInitialisationError.SECRET_UNAVAILABLE]: {
    title: "Can't reach the server",
    body: "ePOCUS needs to reach the server to unlock the scans stored on this device. It will keep trying and will carry on by itself as soon as you are back online.",
    canRetry: true,
  },
  [RxDBInitialisationError.SECRET_REFUSED]: {
    title: "This account can't open ePOCUS yet",
    body: "The server refused to unlock this device for your account. Ask your program administrator to check your access, or sign out and back in once it has been granted.",
    canRetry: true,
  },
  [RxDBInitialisationError.UNSYNCED_LOCAL_DATA]: {
    title: "This device has work that hasn't synced",
    body: "The storage on this device was locked with an older key and still holds data that never reached the server — patient details captured offline, referrals, or scans waiting to sync. Contact the Elephant team before going further. Resetting clears that data permanently and cannot be undone.",
    canRetry: false,
    resetLabel: "Reset this device and continue",
  },
  [RxDBInitialisationError.RECOVERABLE]: STORAGE_COPY,
  [RxDBInitialisationError.FATAL]: STORAGE_COPY,
};

export const EpocusDatabaseGate: FCwC = ({ children }) => {
  const { initialisationError, resetLocalDatabase, canResetLocalDatabase } =
    useRxDBContext();

  return initialisationError === undefined ? (
    <>{children}</>
  ) : (
    <LocalStorageUnavailable
      copy={BLOCKED_COPY[initialisationError]}
      onReset={canResetLocalDatabase ? resetLocalDatabase : undefined}
    />
  );
};

const ResetAction: FC<{ label: string; onReset?: () => Promise<void> }> = ({
  label,
  onReset,
}) => {
  const [confirming, setConfirming] = useState(false);

  if (!onReset) {
    return (
      <Typography variant="caption" style={{ color: EPOCUS_TEXT_SECONDARY }}>
        Resetting this device needs a connection to the server.
      </Typography>
    );
  }

  return (
    <Button
      variant="outlined"
      color="primary"
      onClick={() => (confirming ? onReset() : setConfirming(true))}
    >
      {confirming ? "Yes, delete this device's data" : label}
    </Button>
  );
};

const Screen = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  height: "100dvh",
  padding: rem(24),
  backgroundColor: EPOCUS_BG,
  "@supports not (height: 100dvh)": {
    height: "100vh",
  },
});

const Card = styled(Box)({
  maxWidth: rem(440),
  width: "100%",
  padding: rem(32),
  borderRadius: rem(12),
  backgroundColor: EPOCUS_WHITE,
  textAlign: "center",
});

const Actions = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: rem(12),
  alignItems: "center",
});

export const LocalStorageUnavailable: FC<{
  copy?: BlockedCopy;
  onReset?: () => Promise<void>;
}> = ({ copy = STORAGE_COPY, onReset }) => (
  <Screen data-testid="epocus-local-storage-unavailable">
    <Card>
      <Typography
        variant="h6"
        style={{ color: EPOCUS_TEXT_PRIMARY, marginBottom: rem(12) }}
      >
        {copy.title}
      </Typography>
      <Typography
        variant="body2"
        style={{ color: EPOCUS_TEXT_SECONDARY, marginBottom: rem(24) }}
      >
        {copy.body}
      </Typography>
      <Actions>
        {copy.canRetry && (
          <Button
            variant="contained"
            color="primary"
            onClick={() => window.location.reload()}
          >
            Try again
          </Button>
        )}
        {copy.resetLabel && (
          <ResetAction label={copy.resetLabel} onReset={onReset} />
        )}
        <Button component={Link} to={linkToSignOut()} variant="text">
          Sign out
        </Button>
      </Actions>
    </Card>
  </Screen>
);
